// Canonical experience slugs — single source of truth for routing.
//
// There used to be two parallel systems:
//   /experience/[slug] (editorial, rich storytelling, WhatsApp CTA)
//   /discover/[slug]   (catalogue, pre-book CTA)
// That split was confusing (different hosts, durations, CTAs for the same
// experience). The canonical base is now /experience/[slug] for everything.
// /discover/[slug] only redirects to the canonical page.

import {
  experienceDetails,
  getExperienceDetail,
} from '@/lib/experience-details'
import {
  getHomepageExperienceBySlug,
  homepageExperiences,
} from '@/lib/homepage-experiences'

/** Discovery slug -> canonical /experience slug (correct spelling). */
const DISCOVERY_TO_CANONICAL: Record<string, string> = {
  'thirubuvanam-silk-weaving': 'thirubhuvanam-silk-weaving',
  'kumbakonam-betel-farm-walk': 'kumbakonam-betel-leaf-farm',
  'karuppur-kalamkari': 'kalamkari-painting',
  'thanjavur-thalaiyatti-bommai': 'thanjavur-doll-making',
}

/** Legacy / editorial aliases that should resolve to the canonical slug. */
const ALIAS_TO_CANONICAL: Record<string, string> = {
  ...DISCOVERY_TO_CANONICAL,
  // Misspelt variant without the "h" — redirect to the correct spelling.
  'thirubhuvanam-silk-weaving': 'thirubhuvanam-silk-weaving',
  'kanchi-koil-idli-making': 'traditional-kanchipuram-idli',
  'thooyamalli-paddy-walk': 'paddy-field-experience',
  'thudumbattam-drums-crafting': 'thudumbattam-banana-plantation',
  banana: 'thudumbattam-banana-plantation',
  'kumbakonam-betel-leaf-farm': 'kumbakonam-betel-leaf-farm',
  'thanjavur-doll-making': 'thanjavur-doll-making',
  'kalamkari-painting': 'kalamkari-painting',
}

export function getCanonicalExperienceSlug(slug: string): string {
  return ALIAS_TO_CANONICAL[slug] ?? DISCOVERY_TO_CANONICAL[slug] ?? slug
}

export function getCanonicalExperiencePath(slug: string): string {
  return `/experience/${getCanonicalExperienceSlug(slug)}`
}

/** Editorial record for a slug (resolves aliases first). */
export function getCanonicalEditorial(slug: string) {
  return getExperienceDetail(getCanonicalExperienceSlug(slug))
}

/** Discovery/catalogue record for a canonical slug (checks reverse mapping). */
export function getDiscoveryByCanonical(canonicalSlug: string) {
  const direct = getHomepageExperienceBySlug(canonicalSlug)
  if (direct) return direct
  const discoverySlug = Object.entries(DISCOVERY_TO_CANONICAL).find(
    ([, canonical]) => canonical === canonicalSlug,
  )?.[0]
  return discoverySlug ? getHomepageExperienceBySlug(discoverySlug) : undefined
}

/** Every canonical slug that must render at /experience/[slug]. */
export function allCanonicalExperienceSlugs(): string[] {
  const slugs = new Set<string>()
  for (const { slug } of experienceDetails) slugs.add(slug)
  for (const { slug } of homepageExperiences) {
    slugs.add(getCanonicalExperienceSlug(slug))
  }
  return [...slugs]
}

/** Every path that should exist (canonical + aliases) for static generation. */
export function allExperienceRouteSlugs(): string[] {
  const slugs = new Set<string>(allCanonicalExperienceSlugs())
  for (const alias of Object.keys(ALIAS_TO_CANONICAL)) slugs.add(alias)
  for (const { slug } of homepageExperiences) slugs.add(slug)
  return [...slugs]
}
